import { useId } from 'react';

import {
  PRO_WORDMARK_PATH,
  PRO_WORDMARK_VIEWBOX_HEIGHT,
  PRO_WORDMARK_VIEWBOX_WIDTH,
  WORDMARK_PATH,
  WORDMARK_VIEWBOX_HEIGHT,
  WORDMARK_VIEWBOX_WIDTH,
} from '@splitfiesta/shared';

interface WordmarkProps {
  /** Height of the wordmark in CSS pixels (the width follows the outline). */
  height?: number;
  className?: string;
  /** Keeps the loading screens' pulse. */
  pulse?: boolean;
  /** Draws "SplitFiesta Pro" instead, for the signed-in header of a Pro user. */
  pro?: boolean;
}

/**
 * The "SplitFiesta" wordmark, drawn from the shared outline (Inter Display
 * Bold, generated by apps/mobile/scripts/generate-wordmark.py) so the web
 * shows exactly the letters the mobile header, the splash and the store
 * assets show — no dependency on the Google Fonts request having landed.
 * Gradient is the brand's orange-600 → red-600, as the old
 * `bg-clip-text` version had.
 *
 * The name stays in the DOM as visually-hidden text for screen readers,
 * the page title and tests; the SVG is decorative.
 */
export function Wordmark({
  height = 24,
  className = '',
  pulse = false,
  pro = false,
}: WordmarkProps) {
  const [path, viewBoxWidth, viewBoxHeight] = pro
    ? [
        PRO_WORDMARK_PATH,
        PRO_WORDMARK_VIEWBOX_WIDTH,
        PRO_WORDMARK_VIEWBOX_HEIGHT,
      ]
    : [WORDMARK_PATH, WORDMARK_VIEWBOX_WIDTH, WORDMARK_VIEWBOX_HEIGHT];
  // Per instance: the header and the route-change loader can share a page,
  // and a shared id would paint one with the other's gradient. React 18's
  // ids contain colons, which do not survive inside url(#...).
  const gradientId = `wordmark-grad-${useId().replace(/:/g, '')}`;
  return (
    <span
      className={`inline-flex items-center ${pulse ? 'animate-pulse' : ''} ${className}`}
    >
      <svg
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        height={height}
        width={Math.round((height * viewBoxWidth) / viewBoxHeight)}
        aria-hidden="true"
        focusable="false"
        data-testid="wordmark"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#ea580c" />
            <stop offset="1" stopColor="#dc2626" />
          </linearGradient>
        </defs>
        <path d={path} fill={`url(#${gradientId})`} />
      </svg>
      <span className="sr-only">{pro ? 'SplitFiesta Pro' : 'SplitFiesta'}</span>
    </span>
  );
}
